/**
 * Loyalty Points utilities.
 *
 * Users earn points on completed transactions.
 * Points can be redeemed for discounts on future purchases.
 *
 * Points config (ENV):
 *   LOYALTY_POINTS_PER_EURO — points per EUR spent (default: 10)
 *   LOYALTY_POINTS_REDEMPTION_RATE — EUR per point when redeeming (default: 0.01 = 1 point = €0.01)
 *   LOYALTY_POINTS_EXPIRY_DAYS — days until points expire (default: 365)
 *
 * Storage: user.protectedData.loyaltyPoints = {
 *   balance: number,
 *   lifetime: number,
 *   history: [{ type, points, description, createdAt }]
 * }
 */

export const DEFAULT_POINTS_PER_EURO = parseInt(
  typeof process !== 'undefined' ? (process.env?.LOYALTY_POINTS_PER_EURO || '10') : '10', 10
);
export const DEFAULT_REDEMPTION_RATE = parseFloat(
  typeof process !== 'undefined' ? (process.env?.LOYALTY_POINTS_REDEMPTION_RATE || '0.01') : '0.01'
);
export const HISTORY_MAX = 50;

/**
 * Calculate points earned for a transaction.
 * @param {number} amountCents — transaction amount in cents
 * @param {number} [pointsPerEuro] — points per EUR (default from env)
 * @returns {number} — points earned
 */
export const calculatePointsEarned = (amountCents, pointsPerEuro = DEFAULT_POINTS_PER_EURO) => {
  if (amountCents <= 0) return 0;
  return Math.floor((amountCents / 100) * pointsPerEuro);
};

/**
 * Calculate discount in cents for redeeming points.
 * @param {number} points — points to redeem
 * @param {number} [redemptionRate] — EUR per point
 * @returns {number} — discount in cents
 */
export const calculateRedemptionValue = (points, redemptionRate = DEFAULT_REDEMPTION_RATE) => {
  if (points <= 0) return 0;
  return Math.floor(points * redemptionRate * 100);
};

/**
 * Get points balance from user.
 * @param {Object|null} user
 * @returns {{ balance: number, lifetime: number }}
 */
export const getPointsBalance = (user) => {
  const data = user?.attributes?.protectedData?.loyaltyPoints;
  return { balance: data?.balance || 0, lifetime: data?.lifetime || 0 };
};

/**
 * Format points for display.
 * @param {number} points
 * @returns {string} — e.g. "1,250 pts"
 */
export const formatPoints = (points) =>
  `${points.toLocaleString()} pts`;
